import React, { useState, useEffect } from "react";
import "./SideBar.css";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@aws-amplify/ui-react";
import {
  BiChevronUp,
  BiChevronDown,
  BiChevronLeft,
} from "react-icons/bi";
import { hasModule } from "../../utils/moduleHelpers";

export interface NavItemData {
  eventKey?: string;
  title?: string;
  icon?: any;
  to?: any;
  target?: string;
  children?: NavItemData[];
  moduleName?: string;
  inheritQueryString?: boolean;
}

export interface SidebarNavProps {
  navs?: NavItemData[];
  onCollapseChange?: (collapsed: boolean) => void;
  initialCollapsed?: boolean;
}

const SideBarNav = ({ navs, onCollapseChange, initialCollapsed = true }: SidebarNavProps) => {
  const [openMenu, setOpenMenu] = useState<{ [key: string]: boolean }>({});
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed); // ✅ default collapsed
  const location = useLocation();
  const navigate = useNavigate();

  // Check if any child route is active for a parent menu
  const isChildRouteActive = (children: NavItemData[]) => {
    return children.some(child => {
      const childPath = (child.to ?? '').split('?')[0]; // Handle query params if any
      return location.pathname === childPath || !!childPath && location.pathname.startsWith(childPath + '/');
    });
  };

  // Open parent menu when a child or parent route is active
  useEffect(() => {
    if (navs) {
      // First, find if there's any active submenu
      let activeParent: string | null = null;
      navs.forEach(item => {
        if (item.children && item.eventKey) {
          const parentPath = item.to ? item.to.split('?')[0] : null;
          const isParentActive = parentPath && (location.pathname === parentPath || location.pathname.startsWith(parentPath + '/'));

          if (isParentActive || isChildRouteActive(item.children)) {
            activeParent = item.eventKey;
          }
        }
      });

      // If there's an active submenu, only open that one
      if (activeParent) {
        setOpenMenu({ [activeParent]: true });
      }
    }
  }, [location.pathname, navs]);

  const toggleSidebar = () => {
    const newState = !isCollapsed;
    setIsCollapsed(newState);
    if (onCollapseChange) {
      onCollapseChange(newState);
    }
  };

  const handleParentClick = (eventKey: string) => {
    if (isCollapsed) {
      // ✅ Expand sidebar and open only clicked submenu
      setIsCollapsed(false);
      setOpenMenu({ [eventKey]: true });
      if (onCollapseChange) {
        onCollapseChange(false);
      }
    } else {
      // ✅ Toggle current submenu and close others
      setOpenMenu((prev) => {
        // If the menu is already open, toggle it closed, otherwise open it and close others
        if (prev[eventKey]) {
          return { ...prev, [eventKey]: false };
        } else {
          return { [eventKey]: true };
        }
      });
    }
  };

  return (
    <>
      {/* ✅ Toggle button shown only when sidebar is expanded */}
      {!isCollapsed && (
        <div className="sidebar-toggle-btn" onClick={toggleSidebar}>
          <BiChevronLeft />
        </div>
      )}

      <div className={`sidebar-nav upper-menu ${isCollapsed ? "collapsed" : ""}`}>
        <ul>
          {navs?.map((item) => {
            if (item.moduleName && !hasModule(item.moduleName)) {
              return null;
            }

            const { children, ...rest } = item;

            if (children) {
              return (
                <li key={item.eventKey}>
                  <div
                    onClick={() => handleParentClick(item.eventKey!)}
                    className="menu-item"
                  >
                    <NavLink
                      to={item.to || "#"}
                      className={({ isActive }) =>
                        ((item.to && item.to !== "#" && isActive) || isChildRouteActive(children)) ? "active" : undefined
                      }
                      onClick={(e) => {
                        if (!item.to) {
                          e.preventDefault();
                        }
                      }}
                    >
                      <Icon>{item.icon}</Icon>
                      <span> {!isCollapsed && item.title} </span>
                    </NavLink>
                    {!isCollapsed && (
                      <span className="collapseIcon">
                        {openMenu[item.eventKey!] ? (
                          <BiChevronUp />
                        ) : (
                          <BiChevronDown />
                        )}
                      </span>
                    )}
                  </div>
                  {!isCollapsed && openMenu[item.eventKey!] && (
                    <ul className="submenu">
                      {children.map((child) => {
                        if (child.moduleName && !hasModule(child.moduleName)) {
                          return null;
                        }
                        return (
                          <li key={child.eventKey}>
                            <NavLink
                              to={child.to}
                              className={({ isActive }) =>
                                isActive ? "active" : undefined
                              }
                              onClick={(e) => {
                                if (child.inheritQueryString) {
                                  e.preventDefault();
                                  const storedProject = sessionStorage.getItem('cg-selected-project');
                                  const cleanTo = child.to ? child.to.split('?')[0] : '';
                                  if (storedProject) {
                                    const params = new URLSearchParams();
                                    params.set('project', storedProject);
                                    const search = params.toString();
                                    window.history.replaceState(null, '', `${cleanTo}?${search}`);
                                    navigate(cleanTo + (search ? `?${search}` : ''));
                                  } else {
                                    window.history.replaceState(null, '', cleanTo);
                                    navigate(cleanTo);
                                  }
                                  return;
                                }
                                const href = child.to || '';
                                if (typeof href === 'string' && href.includes('/qanewproject')) {
                                  e.preventDefault();
                                  const qs = window.location.search || '';
                                  window.location.href = href + qs;
                                  return;
                                }
                                if (isCollapsed) {
                                  setIsCollapsed(false);
                                  if (onCollapseChange) onCollapseChange(false);
                                }
                                setOpenMenu({});
                              }}
                            >
                              {child.title}
                            </NavLink>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            }

            if (rest.target === "_blank") return null;

            return (
              <li key={item.eventKey}>
                <NavLink
                  to={item.to}
                  onClick={() => {
                    if (isCollapsed) {
                      setIsCollapsed(false);
                      if (onCollapseChange) {
                        onCollapseChange(false);
                      }
                    }
                    // Close all open submenus when clicking on a regular menu item
                    setOpenMenu({});
                  }}
                  className={({ isActive }) =>
                    isActive ? "active" : undefined
                  }
                >
                  <Icon>{item.icon}</Icon>
                  <span>{!isCollapsed && item.title}</span>
                </NavLink>
              </li>
            );
          })}
        </ul>
      </div>
    </>
  );
};

export default SideBarNav;
